import { Injectable } from "@angular/core";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { ITimelineFilter } from "../../interfaces/i-timeline-filter";
import { ITypeFilter } from "../../interfaces/i-type-filter";
import { TimelineFilter } from "../../model/timeline-filter";
import { TypeFilter } from "../../model/type-filter";

@Injectable({
  providedIn: "root",
})
export class FilterProjectService {
  static emptyProjectsFilter(): ITimelineFilter {
    return TimelineFilter(
      [TypeFilter("2D", "2D"), TypeFilter("3D", "3D")],
      [TypeFilter(5, 5), TypeFilter(7.5, 7.5), TypeFilter(15, 15), TypeFilter(20, 20)],
      [TypeFilter("True Ortho", "final-ortho"), TypeFilter("Ortho", "quick-ortho")],
      [TypeFilter("Blue Sky", "bluesky"), TypeFilter("Gray Sky", "graysky")],
    );
  }

  static emptyCollectionFilter(): ITimelineFilter {
    return TimelineFilter(null, null, null, null, [
      TypeFilter("Urban", "urban,urban-g", "3D", "blue", "7.5 cm"),
      TypeFilter("Urban R", "urban-r", "2D", "blue", "7.5 cm"),
      TypeFilter("Wide-Area", "wide-area", "2D", "light-blue", "15-20 cm"),
      TypeFilter("Graysky", "graysky,graysky-g", "2D", "graysky", "7.5-20 cm"),
    ]);
  }

  static populateFilter(filter: ITimelineFilter, data: ITimelineFilter): ITimelineFilter {
    //change this for something dynamic
    filter.modeTypes?.forEach(
      (f, i) => (f.checked = data.modeTypes?.length > 0 ? data.modeTypes[i]?.checked : f.checked),
    );
    filter.orthoStatus?.forEach(
      (f, i) => (f.checked = data.orthoStatus?.length > 0 ? data.orthoStatus[i]?.checked : f.checked),
    );
    filter.imageryTypes?.forEach(
      (f, i) => (f.checked = data.imageryTypes?.length > 0 ? data.imageryTypes[i]?.checked : f.checked),
    );
    filter.resolutions?.forEach(
      (f, i) => (f.checked = data.resolutions?.length > 0 ? data.resolutions[i]?.checked : f.checked),
    );
    filter.layerTypes?.forEach(
      (f, i) => (f.checked = data.layerTypes?.length > 0 ? data.layerTypes[i]?.checked : f.checked),
    );
    return filter;
  }

  static reduce(filter: ITypeFilter): any {
    return {
      title: filter.title,
      value: filter.value,
      checked: filter.checked,
    };
  }

  /**
   * Reset counters in filters
   * Before filter the projects with a new filter object, we reset the counter for each filter
   */
  static resetFiltersCount(filter: ITimelineFilter) {
    //todo change for something more dynamic
    filter.modeTypes?.forEach((i) => FilterProjectService.resetFilter(i));
    filter.orthoStatus?.forEach((i) => FilterProjectService.resetFilter(i));
    filter.imageryTypes?.forEach((i) => FilterProjectService.resetFilter(i));
    filter.resolutions?.forEach((i) => FilterProjectService.resetFilter(i));
    filter.layerTypes?.forEach((i) => FilterProjectService.resetFilter(i));
  }

  static resetFilter(f: ITypeFilter) {
    f.match = 0;
    f.visible = 0;
    f.available = 0;
    f.available3D = 0;
  }

  /**
   * Update type filter value
   * Increase match counter because match with this filter
   * Increase available if match and is enabled (available in extent)
   * Increase exclude if is not previously excluded by other filter and is not checked
   * @param filter
   * @param aoi
   * @param included if is still included in the filter
   */
  static projects(filter: ITypeFilter, aoi: IProjectAOI, included: boolean) {
    if (!filter) {
      return false;
    }

    const exclude = included && !filter?.checked; // calculate if this filter exclude the aoi for the filter
    filter.match += 1;
    filter.available += Number(aoi.isEnabled());
    filter.available3D += Number(aoi.isEnabled3D() && aoi.isAOIOblique && aoi.isEnabled());
    return included && !exclude; // if previously excluded return false if not the current value
  }

  /**
   * Update visible count for each filter category
   * Only apply for projects visibles after apply main filter
   * @param filter
   * @param project
   */
  static projectsExcluded(filter: ITypeFilter, project: IProjectAOI) {
    if (project.isEnabled() && project.isVisible()) {
      filter.visible += Number(project.isEnabled() && project.isVisible()); // if is previously filtered
    }
  }

  /**
   * Iterate trough visible projects after filter and update filter numbers availability
   * We have to wait until projects change the visibility
   * @param projects
   * @param filter
   */
  static processAfterFilter(projects: IProjectAOI[], filter: ITimelineFilter) {
    //todo change for something more dynamic
    projects.forEach((p) => {
      // update excluded count, after filter projects
      if (!!filter.modeTypes) {
        const modeFilter = this.getModeFilterByProject(filter, p);
        FilterProjectService.projectsExcluded(modeFilter, p);
      }
      if (!!filter.resolutions) {
        const resolutionFilter = this.getResolutionFilterByProject(filter, p);
        FilterProjectService.projectsExcluded(resolutionFilter, p);
      }
      if (!!filter.orthoStatus) {
        const orthoFilter = this.getOrthoStatusFilterByProject(filter, p);
        FilterProjectService.projectsExcluded(orthoFilter, p);
      }
      if (!!filter.imageryTypes) {
        const imageryFilter = this.getImageryTypeFilterByProject(filter, p);
        FilterProjectService.projectsExcluded(imageryFilter, p);
      }
      if (!!filter.layerTypes) {
        const layerFilter = this.getLayerTypeFilterByProject(filter, p);
        FilterProjectService.projectsExcluded(layerFilter, p);
      }

      return p;
    });
  }

  /**
   * Filter list of projects by certain criteria
   * @param projects
   * @param filter
   */
  static filterProjects(projects: IProjectAOI[], filter: ITimelineFilter) {
    const match = [];
    const excluded = [];

    if (projects?.length) {
      // count excluded projects in each filter
      projects.forEach((a) => {
        let include = FilterProjectService.filterProject(a, filter);

        if (include) {
          match.push(a);
        } else {
          excluded.push(a);
        }
      });
    }
    return match;
  }

  static filterProject(project: IProjectAOI, filter: ITimelineFilter) {
    let include = true;
    if (!!filter.modeTypes) {
      const modeFilter = this.getModeFilterByProject(filter, project);
      include = FilterProjectService.projects(modeFilter, project, include);
    }

    if (!!filter.imageryTypes) {
      const imageryFilter = this.getImageryTypeFilterByProject(filter, project);
      include = FilterProjectService.projects(imageryFilter, project, include);
    }

    if (!!filter.orthoStatus) {
      const orthosFilter = this.getOrthoStatusFilterByProject(filter, project);
      include = FilterProjectService.projects(orthosFilter, project, include);
    }

    if (!!filter.resolutions) {
      const resolutionFitler = this.getResolutionFilterByProject(filter, project);
      include = FilterProjectService.projects(resolutionFitler, project, include);
    }

    if (!!filter.layerTypes) {
      const layerFilter = this.getLayerTypeFilterByProject(filter, project);
      include = FilterProjectService.projects(layerFilter, project, include);
    }

    return include;
  }

  static resolutionMinValue(r: ITypeFilter[], i: number) {
    return i > 0 ? r[i].value - Math.abs(r[i - 1].value - r[i].value) / 2 : r[i].value / 2;
  }

  static resolutionMaxValue(r: ITypeFilter[], i: number) {
    return i < r.length - 1 ? r[i].value + Math.abs(r[i + 1].value - r[i].value) / 2 : r[i].value + r[i].value / 2;
  }

  /**
   * Check if project match with the criteria defined in the filter

   * @param filter
   * @param project
   */
  static matchWithFilter(filter: ITimelineFilter, project: IProjectAOI): boolean {
    let match = true;
    //check if match with the criteria, find mode type if is checked means that match
    if (!!filter.modeTypes) {
      match = FilterProjectService.getModeFilterByProject(filter, project).checked;
      if (!match) return match; // break if not match the filter
    }

    //check if match with the criteria, find coverage type if is checked means that match
    if (!!filter.orthoStatus) {
      match = FilterProjectService.getOrthoStatusFilterByProject(filter, project).checked;
      if (!match) return match; // break if not match the filter
    }

    //check if match with the criteria, find collection type if is checked means that match
    if (!!filter.imageryTypes) {
      match = FilterProjectService.getImageryTypeFilterByProject(filter, project).checked;
      if (!match) return match; // break if not match the filter
    }

    //check if match with the criteria, find resolution if is checked means that match
    if (!!filter.resolutions) {
      match = FilterProjectService.getResolutionFilterByProject(filter, project).checked;
      if (!match) return match; // break if not match the filter
    }

    //check if match with the criteria, find layer type if is checked means that match
    if (!!filter.layerTypes) {
      match = FilterProjectService.getLayerTypeFilterByProject(filter, project).checked;
      return match; // break if not match the filter
    }
    return match;
  }

  /**
   * Iterate through filter properties and match with a project, if the criteria not match we update the filter to match with that project
   * @param filter
   * @param project
   */
  static updateFilterByProject(filter: ITimelineFilter, project: IProjectAOI): ITimelineFilter {
    //update filter with project mode
    if (!!filter.modeTypes) {
      FilterProjectService.getModeFilterByProject(filter, project).checked = true;
    }

    //update filter with project coverage type
    if (!!filter.orthoStatus) {
      FilterProjectService.getOrthoStatusFilterByProject(filter, project).checked = true;
    }

    //update filter with imagery type
    if (!!filter.imageryTypes) {
      FilterProjectService.getImageryTypeFilterByProject(filter, project).checked = true;
    }

    //update filter with resolution
    if (!!filter.resolutions) {
      FilterProjectService.getResolutionFilterByProject(filter, project).checked = true;
    }

    //update filter with layer  type
    if (!!filter.layerTypes) {
      FilterProjectService.getLayerTypeFilterByProject(filter, project).checked = true;
    }

    return filter;
  }

  static getModeFilterByProject(filter: ITimelineFilter, project: IProjectAOI) {
    const projectMode = project.isAOIOblique()
      ? ViewerConstants.COLLECTION_MODES.OBLIQUE.name
      : ViewerConstants.COLLECTION_MODES.VERTICAL.name;
    return filter.modeTypes.find((mode) => mode.value === projectMode);
  }

  static getOrthoStatusFilterByProject(filter: ITimelineFilter, project: IProjectAOI) {
    const projectCoverage =
      project.getCoverageType() !== ViewerConstants.COVERAGE_TYPE_STATUS.QUICK &&
      project.getCoverageType() !== ViewerConstants.COVERAGE_TYPE_STATUS.FIRST &&
      !project.isWide()
        ? ViewerConstants.COVERAGE_TYPE_STATUS.FINAL
        : ViewerConstants.COVERAGE_TYPE_STATUS.QUICK;
    return filter.orthoStatus.find((status) => status.value === projectCoverage);
  }

  static getImageryTypeFilterByProject(filter: ITimelineFilter, project: IProjectAOI) {
    const projectImagery = project.getCollectionType();
    return filter.imageryTypes.find((imagery) => imagery.value === projectImagery);
  }

  static getResolutionFilterByProject(filter: ITimelineFilter, project: IProjectAOI) {
    const projectResolution = FilterProjectService.projectResolution(filter, project);
    return filter.resolutions.find((resolution) => resolution.value === projectResolution.value);
  }

  static getLayerTypeFilterByProject(filter: ITimelineFilter, project: IProjectAOI) {
    const projectLayer = project.getLayerName();
    return filter.layerTypes.find((lt) => {
      const values = lt.value.split(",");
      return values.includes(projectLayer);
    });
  }

  static projectResolution(filter: ITimelineFilter, project: IProjectAOI): ITypeFilter {
    let resolution: ITypeFilter, maxValue: number, minValue: number;
    const averageGsd = project.getAvgGsd() * 100;
    filter.resolutions.forEach((r, index) => {
      minValue = FilterProjectService.resolutionMinValue(filter.resolutions, index);
      maxValue = FilterProjectService.resolutionMaxValue(filter.resolutions, index);
      const isInRange = averageGsd >= minValue && averageGsd <= maxValue;
      if (!resolution && isInRange) {
        resolution = r;
      }
      if (index === filter.resolutions.length - 1 && !resolution) {
        resolution = r;
      }
    });
    return resolution;
  }
}
